import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import ApiService from "../../services/api.service";
import { LiveGameBadge, LiveScorerLine } from "../../components/shared/LiveGameBadge";
import { localWhen, localWhenLine } from "../../utils/gameTime";
import { useRole } from "../../components/shared/RoleContext";
import { SPORT_STATS, DEFAULT_SPORT } from "../../config/sportStats";
import "../client/ClientLayout.css";
import "./CoachPages.css";


// Columns filtered to stats someone on this team actually recorded — mirrors
// MatchmakingView.BoxScore's convention.
function usedColumns(sport, players) {
  const fields = SPORT_STATS[sport] || SPORT_STATS[DEFAULT_SPORT];
  return fields.filter((f) => players.some((p) => (p.stats?.[f.key] || 0) !== 0));
}

function BoxScoreTable({ name, players, sport }) {
  const columns = usedColumns(sport, players);
  if (players.length === 0) {
    return (
      <div className="coach-boxscore-team">
        <div className="coach-boxscore-team-name">{name}</div>
        <div className="client-loading-row">No stats recorded</div>
      </div>
    );
  }
  return (
    <div className="coach-boxscore-team">
      <div className="coach-boxscore-team-name">{name}</div>
      <div className="coach-boxscore-scroll">
        <table className="coach-boxscore-table">
          <thead>
            <tr>
              <th>Player</th>
              {columns.map((f) => <th key={f.key}>{f.abbr}</th>)}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.clientId}>
                <td>{p.number ? `#${p.number} ` : ""}{p.name}</td>
                {columns.map((f) => <td key={f.key}>{Math.round(p.stats?.[f.key] || 0)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Which of the coach's teams this game belongs to — only rendered once the
// coach has 2+ teams (mirrors iOS's CoachGamesScreen.teamBadge), so a
// single-team coach's rows are unchanged.
function TeamBadge({ game, showTeamBadge }) {
  if (!showTeamBadge || !game.myTeamName) return null;
  return <span className="coach-team-badge">{game.myTeamName}</span>;
}

// A tournament game is just a LeagueGames row with these extra fields —
// mirrors LeagueSchedulePage's badge exactly.
function TournamentBadge({ game }) {
  if (!game.tournamentRoundName) return null;
  return (
    <div className="coach-tournament-badge">
      <Trophy size={12} /> {game.tournamentName || "Tournament"} · {game.tournamentRoundName}
    </div>
  );
}

function UpcomingRow({ game, showTeamBadge }) {
  return (
    <div className="coach-history-card">
      <div className="coach-history-row" style={{ cursor: "default" }}>
        <div>
          <TeamBadge game={game} showTeamBadge={showTeamBadge} />
          <div className="coach-history-matchup">{game.teamAName} vs {game.teamBName}</div>
          <div className="coach-history-date">{localWhenLine(game)}</div>
          <TournamentBadge game={game} />
          {/* A coach never scores — this is context. Worth showing anyway: a
              live feed that has gone quiet usually means the keeper stopped,
              which is exactly what a coach watching from the bench wants. */}
          <LiveScorerLine game={game} />
        </div>
        <div className={`coach-history-score${game.officialId || game.isLive ? "" : " coach-unassigned"}`}>
          {game.isLive ? <LiveGameBadge game={game} /> : (game.officialName || "No official assigned")}
        </div>
      </div>
    </div>
  );
}

function HistoryRow({ game, showTeamBadge }) {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !summary) {
      setLoading(true);
      try {
        const resp = await ApiService.getCoachGameStats(game.gameId);
        setSummary(resp);
      } catch {
        // leave summary null — the panel shows nothing further to load
      } finally {
        setLoading(false);
      }
    }
  };

  const scoreText =
    game.scoreA != null && game.scoreB != null
      ? `Final ${Math.round(game.scoreA)} – ${Math.round(game.scoreB)}`
      : "Final";

  return (
    <div className="coach-history-card">
      <button className="coach-history-row" onClick={toggle}>
        <div>
          <TeamBadge game={game} showTeamBadge={showTeamBadge} />
          <div className="coach-history-matchup">{game.teamAName} vs {game.teamBName}</div>
          <div className="coach-history-date">{localWhen(game).date}</div>
          <TournamentBadge game={game} />
        </div>
        <div className="coach-history-score">{scoreText}</div>
      </button>
      {expanded && (
        <div className="coach-history-detail">
          {loading ? (
            <div className="client-loading-row">Loading box score…</div>
          ) : summary ? (
            <>
              <BoxScoreTable
                name={summary.teamA?.businessName || game.teamAName || "Team A"}
                players={(summary.players || []).filter((p) => p.team !== "B")}
                sport={summary.sport}
              />
              <BoxScoreTable
                name={summary.teamB?.businessName || game.teamBName || "Team B"}
                players={(summary.players || []).filter((p) => p.team === "B")}
                sport={summary.sport}
              />
            </>
          ) : (
            <div className="client-error">Couldn't load this game's stats.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CoachGamesPage() {
  const { role } = useRole();
  const showTeamBadge = (role?.teamCount || 0) > 1;
  const [scope, setScope] = useState("upcoming");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await ApiService.getCoachGames(scope);
        setGames(resp.games || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [scope]);

  return (
    <div>
      <div className="coach-page-header">
        <h1 className="client-h1">Games</h1>
      </div>
      <div className="coach-scope-toggle">
        <button
          className={scope === "upcoming" ? "active" : ""}
          onClick={() => setScope("upcoming")}
        >
          Upcoming
        </button>
        <button
          className={scope === "history" ? "active" : ""}
          onClick={() => setScope("history")}
        >
          Past
        </button>
      </div>

      {loading ? (
        <div className="client-loading-row">Loading…</div>
      ) : error ? (
        <div className="client-error">{error}</div>
      ) : games.length === 0 ? (
        <div className="client-empty">
          {scope === "upcoming" ? "No upcoming games yet." : "No completed games yet."}
        </div>
      ) : (
        <div className="coach-history-list">
          {games.map((g) =>
            scope === "upcoming"
              ? <UpcomingRow key={g.gameId} game={g} showTeamBadge={showTeamBadge} />
              : <HistoryRow key={g.gameId} game={g} showTeamBadge={showTeamBadge} />
          )}
        </div>
      )}
    </div>
  );
}
